import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Snackbar, Chip, Tooltip,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useQuery, useMutation } from '@apollo/client';
import {
  PERMISSIONS_QUERY, ROLES_QUERY, SUBSCRIPTIONS_QUERY,
  CREATE_PERMISSION_MUTATION, UPDATE_PERMISSION_MUTATION, DELETE_PERMISSION_MUTATION,
  ASSIGN_PERMISSION_TO_ROLE_MUTATION, REMOVE_PERMISSION_FROM_ROLE_MUTATION,
  ASSIGN_PERMISSION_TO_SUBSCRIPTION_MUTATION, REMOVE_PERMISSION_FROM_SUBSCRIPTION_MUTATION,
} from '../graphql/queries';

const EMPTY_FORM = { name: '', displayName: '', description: '' };

export default function AdminPermissionsCrudPage() {
  const { data, loading, error, refetch } = useQuery(PERMISSIONS_QUERY);
  const { data: rolesData }   = useQuery(ROLES_QUERY);
  const { data: subsData }    = useQuery(SUBSCRIPTIONS_QUERY);

  const [createPerm]   = useMutation(CREATE_PERMISSION_MUTATION);
  const [updatePerm]   = useMutation(UPDATE_PERMISSION_MUTATION);
  const [deletePerm]   = useMutation(DELETE_PERMISSION_MUTATION);
  const [assignToRole] = useMutation(ASSIGN_PERMISSION_TO_ROLE_MUTATION);
  const [removeFromRole] = useMutation(REMOVE_PERMISSION_FROM_ROLE_MUTATION);
  const [assignToSub]  = useMutation(ASSIGN_PERMISSION_TO_SUBSCRIPTION_MUTATION);
  const [removeFromSub] = useMutation(REMOVE_PERMISSION_FROM_SUBSCRIPTION_MUTATION);

  const [dialog, setDialog]   = useState({ open: false, mode: 'create', item: EMPTY_FORM });
  const [assignDialog, setAssignDialog] = useState({ open: false, permId: null, permName: '' });
  const [saving, setSaving]   = useState(false);
  const [snack, setSnack]     = useState({ open: false, msg: '', severity: 'success' });
  const [confirmDel, setConfirmDel] = useState(null);

  const permissions  = data?.permissions   || [];
  const roles        = rolesData?.roles    || [];
  const subs         = subsData?.subscriptions || [];

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const openCreate = () => setDialog({ open: true, mode: 'create', item: EMPTY_FORM });
  const openEdit   = (p)  => setDialog({ open: true, mode: 'edit', item: { id: p.id, name: p.name, displayName: p.displayName, description: p.description || '' } });

  const handleSave = async () => {
    const { mode, item } = dialog;
    if (!item.displayName) return;
    setSaving(true);
    try {
      if (mode === 'create') {
        if (!item.name) return;
        await createPerm({ variables: { input: { name: item.name, displayName: item.displayName, description: item.description } } });
        showSnack('Permission created');
      } else {
        await updatePerm({ variables: { input: { id: item.id, displayName: item.displayName, description: item.description } } });
        showSnack('Permission updated');
      }
      await refetch();
      setDialog({ open: false, mode: 'create', item: EMPTY_FORM });
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await deletePerm({ variables: { id } });
      await refetch();
      setConfirmDel(null);
      showSnack('Permission deleted');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleAssignRole = async (roleId, has) => {
    const { permId } = assignDialog;
    try {
      if (has) await removeFromRole({ variables: { roleId, permissionId: permId } });
      else     await assignToRole({ variables: { roleId, permissionId: permId } });
      await refetch();
    } catch (e) { showSnack(e.message, 'error'); }
  };

  const handleAssignSub = async (subId, has) => {
    const { permId } = assignDialog;
    try {
      if (has) await removeFromSub({ variables: { subscriptionId: subId, permissionId: permId } });
      else     await assignToSub({ variables: { subscriptionId: subId, permissionId: permId } });
      await refetch();
    } catch (e) { showSnack(e.message, 'error'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 3 }}>{error.message}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <VpnKeyIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Permissions</Typography>
            <Typography variant="body2" color="text.secondary">
              Create, edit and assign platform permissions to roles and plans
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Permission
        </Button>
      </Stack>

      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
              <TableCell>Name (code)</TableCell>
              <TableCell>Display Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="center">Roles</TableCell>
              <TableCell align="center">Plans</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {permissions.map(p => {
              const roleCount = roles.filter(r => r.permissions?.some(rp => rp.id === p.id)).length;
              const subCount  = subs.filter(s => s.permissions?.some(sp => sp.id === p.id)).length;
              return (
                <TableRow key={p.id} hover>
                  <TableCell><code style={{ fontSize: 12, background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>{p.name}</code></TableCell>
                  <TableCell fontWeight={500}>{p.displayName}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontSize: 13 }}>{p.description || '—'}</TableCell>
                  <TableCell align="center">
                    <Chip label={roleCount} size="small" color={roleCount > 0 ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={subCount} size="small" color={subCount > 0 ? 'secondary' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Assign to roles/plans">
                      <IconButton size="small" color="info" onClick={() => setAssignDialog({ open: true, permId: p.id, permName: p.displayName })}>
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(p)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setConfirmDel(p.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {permissions.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>No permissions found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create / Edit dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === 'create' ? 'New Permission' : 'Edit Permission'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {dialog.mode === 'create' && (
              <TextField label="Name (code key)" value={dialog.item.name}
                onChange={e => setDialog(d => ({ ...d, item: { ...d.item, name: e.target.value.toLowerCase().replace(/ /g,'_') } }))}
                placeholder="e.g. manage_contracts" size="small" required fullWidth />
            )}
            <TextField label="Display Name" value={dialog.item.displayName}
              onChange={e => setDialog(d => ({ ...d, item: { ...d.item, displayName: e.target.value } }))}
              size="small" required fullWidth />
            <TextField label="Description" value={dialog.item.description}
              onChange={e => setDialog(d => ({ ...d, item: { ...d.item, description: e.target.value } }))}
              size="small" multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignDialog.open} onClose={() => setAssignDialog(d => ({ ...d, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle>Assign: {assignDialog.permName}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" fontWeight={700} mt={1} mb={1}>Roles</Typography>
          <Stack spacing={0.8}>
            {roles.map(r => {
              const has = r.permissions?.some(rp => rp.id === assignDialog.permId);
              return (
                <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ p: 1, borderRadius: 1, bgcolor: has ? 'success.50' : 'grey.50', border: '1px solid', borderColor: has ? 'success.200' : 'grey.200' }}>
                  <Typography variant="body2">{r.displayName}</Typography>
                  <Button size="small" color={has ? 'error' : 'success'} variant="outlined"
                    onClick={() => handleAssignRole(r.id, has)}>
                    {has ? 'Remove' : 'Add'}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
          <Typography variant="subtitle2" fontWeight={700} mt={2} mb={1}>Subscription Plans</Typography>
          <Stack spacing={0.8}>
            {subs.map(s => {
              const has = s.permissions?.some(sp => sp.id === assignDialog.permId);
              return (
                <Stack key={s.id} direction="row" justifyContent="space-between" alignItems="center"
                  sx={{ p: 1, borderRadius: 1, bgcolor: has ? 'success.50' : 'grey.50', border: '1px solid', borderColor: has ? 'success.200' : 'grey.200' }}>
                  <Typography variant="body2">{s.displayName}</Typography>
                  <Button size="small" color={has ? 'error' : 'success'} variant="outlined"
                    onClick={() => handleAssignSub(s.id, has)}>
                    {has ? 'Remove' : 'Add'}
                  </Button>
                </Stack>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(d => ({ ...d, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onClose={() => setConfirmDel(null)}>
        <DialogTitle>Delete Permission?</DialogTitle>
        <DialogContent><Typography>This will remove the permission from all roles and plans.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(confirmDel)} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
